import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit as limitQuery,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

// Real-time listener for user's notifications
export const useMyNotifications = (userId, limit = null) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    let q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (limit) {
      q = query(q, limitQuery(limit));
    }

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching notifications:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, limit]);

  return { notifications, loading };
};

// Get unread count
export const useUnreadNotificationCount = (userId) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId) {
      setCount(0);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setCount(snap.size);
      },
      (err) => {
        console.error('Error fetching unread count:', err);
      }
    );

    return unsubscribe;
  }, [userId]);

  return count;
};

// Get unread count by notification type
export const useUnreadNotificationCountByType = (userId, types) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId || !types || types.length === 0) {
      setCount(0);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false),
      where('type', 'in', types)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setCount(snap.size);
      },
      (err) => {
        console.error('Error fetching unread count by type:', err);
      }
    );

    return unsubscribe;
  }, [userId, types]);

  return count;
};

// Create notification
export const createNotification = async (userId, type, title, message, bountyId, bountyTitle, claimId, actionUrl) => {
  try {
    await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      title,
      message,
      bountyId: bountyId || null,
      bountyTitle: bountyTitle || null,
      claimId: claimId || null,
      actionUrl,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Error creating notification:', err);
    throw err;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
    });
  } catch (err) {
    console.error('Error marking notification as read:', err);
    throw err;
  }
};

// Mark notifications as read for a specific bounty
export const markNotificationsAsReadForBounty = async (userId, bountyId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('bountyId', '==', bountyId),
      where('read', '==', false)
    );

    const snap = await getDocs(q);
    if (snap.empty) return;

    const batch = writeBatch(db);
    snap.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true });
    });
    await batch.commit();
  } catch (err) {
    console.error('Error marking bounty notifications as read:', err);
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const snap = await getDocs(q);

    if (snap.empty) return;

    const batch = writeBatch(db);

    snap.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true });
    });

    await batch.commit();
  } catch (err) {
    console.error('Error marking all notifications as read:', err);
    throw err;
  }
};
